import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { Text, useModalState } from "@src/appV2/redesign/system/ui-components";
import { type HcfTimeclockComplianceVideoConfig } from "@src/appV2/Shifts/Shift/ShiftState/types";
import { ClockActionPictureDialogEvent } from "@src/appV2/Shifts/Shift/TakeClockActionPicture/ClockActionPictureDialogEvent";
import { getPermissions } from "@src/appV2/Shifts/Shift/TakeClockActionPicture/utils";
import { type ReactNode, useState } from "react";

import { CameraPermissionBottomSheet } from "./dialogs/CameraPermissionBottomSheet";
import { VideoProofPolicyDialog } from "./dialogs/VideoProofPolicyDialog";
import { VideoRecordingDialog } from "./dialogs/VideoRecordingDialog";
import { VideoUploadErrorBottomSheet } from "./dialogs/VideoUploadErrorBottomSheet";
import { VideoUploadProgressDialog } from "./dialogs/VideoUploadProgressDialog";
import { useTimeclockVideoUpload } from "./hooks/useTimeclockVideoUpload";

export interface ComplianceProofVideo {
  bucketName: string;
  fileKey: string;
}

export interface TimeclockVideoProofFlowProps {
  workplaceName: string;
  timeclockLocation: string | undefined;
  timeclockPin: string | undefined;
  timeclockGifUrl: string | undefined;
  workplaceId: string;
  shiftId: string;
  workerId: string;
  clockAction: "clock-in" | "clock-out";
  videoConfig: HcfTimeclockComplianceVideoConfig | undefined;
  linearProgressDurationSeconds?: number;
  isSkipTimeclockUsageEnabled?: boolean;
  timeClockUsageWarning?: string;
  videoReviewNotice?: ReactNode;
  onSuccess: (video: ComplianceProofVideo) => Promise<void>;
  onCancel: () => void;
}

export function TimeclockVideoProofFlow(props: TimeclockVideoProofFlowProps) {
  const {
    workplaceName,
    timeclockLocation,
    timeclockPin,
    timeclockGifUrl,
    workplaceId,
    shiftId,
    workerId,
    clockAction,
    videoConfig,
    linearProgressDurationSeconds,
    isSkipTimeclockUsageEnabled = true,
    timeClockUsageWarning = "Failure to use the time clock may result in DNR or pay reversal.",
    videoReviewNotice = (
      <Text variant="body1">
        This video will be reviewed by facility admin.{" "}
        <Text
          sx={{
            color: (theme) => theme.palette.error.main,
            display: "inline",
          }}
        >
          Repeated failure to use the time clock may result in DNRs or pay reversals.
        </Text>
      </Text>
    ),
    onSuccess,
    onCancel,
  } = props;

  const [currentPage, setCurrentPage] = useState<"policy" | "record-video">("policy");

  const policyModalState = useModalState("open");
  const recordingModalState = useModalState("closed");
  const camereAccessModalState = useModalState();
  const uploadProgressModalState = useModalState();
  const errorModalState = useModalState();

  const logData = {
    video: true,
    clockAction,
    shiftId,
    workerId,
    workplaceId,
    timeclockLocation,
    pinLength: timeclockPin ? timeclockPin.length : undefined,
  };

  const logTimeclockEvent = (
    eventAction: ClockActionPictureDialogEvent,
    meta?: Record<string, any>
  ) => {
    logEvent(APP_V2_USER_EVENTS.FACILITY_TIMECLOCK_COMPLIANCE_EVENT, {
      ...logData,
      ...meta,
      eventAction,
    });
  };

  const {
    isUploading,
    progress: uploadProgress,
    errorText: videoErrorText,
    videoBlob,
    startUpload,
    retryUpload,
    abortUpload,
    reset: resetUpload,
  } = useTimeclockVideoUpload({
    clockAction,
    shiftId,
    workerId,
    workplaceId,
    logData,
    onSuccess,
    logTimeclockEvent,
    onUploadStart: () => {
      uploadProgressModalState.openModal();
    },
    onUploadEnd: () => {
      uploadProgressModalState.closeModal();
    },
    onError: () => {
      errorModalState.openModal();
    },
    linearProgressDurationSeconds,
  });

  const continueToRecording = async (source: "main-dialog" | "help-dialog") => {
    const { camera, microphone } = await getPermissions();

    if (!camera || !microphone) {
      camereAccessModalState.openModal();
      return;
    }

    logTimeclockEvent(ClockActionPictureDialogEvent.POLICY_ACCEPTED);
    if (source === "help-dialog") {
      logTimeclockEvent(ClockActionPictureDialogEvent.USED_FALLBACK_POLICY);
    }

    policyModalState.closeModal();
    recordingModalState.openModal();
    setCurrentPage("record-video");
  };

  return (
    <>
      {currentPage === "policy" && (
        <VideoProofPolicyDialog
          modalState={policyModalState}
          workplaceName={workplaceName}
          clockAction={clockAction}
          timeclockLocation={timeclockLocation}
          timeclockPin={timeclockPin}
          timeclockGifUrl={timeclockGifUrl}
          isSkipTimeclockUsageEnabled={isSkipTimeclockUsageEnabled}
          timeClockUsageWarning={timeClockUsageWarning}
          onContinue={continueToRecording}
          onCancel={() => {
            policyModalState.closeModal();
            onCancel();
            logTimeclockEvent(ClockActionPictureDialogEvent.POLICY_CANCELLED);
          }}
        />
      )}
      {currentPage === "record-video" && (
        <VideoRecordingDialog
          modalState={recordingModalState}
          videoConfig={videoConfig}
          logData={logData}
          isUploading={isUploading}
          videoReviewNotice={videoReviewNotice}
          onRecordVideoSuccess={async (blob) => {
            await startUpload(blob);
          }}
          onClose={() => {
            recordingModalState.closeModal();
            policyModalState.openModal();
            resetUpload();
            setCurrentPage("policy");
          }}
        />
      )}
      <CameraPermissionBottomSheet
        modalState={camereAccessModalState}
        onContinue={camereAccessModalState.closeModal}
        onCancel={camereAccessModalState.closeModal}
      />
      <VideoUploadProgressDialog
        modalState={uploadProgressModalState}
        uploadProgress={uploadProgress}
        onCancel={abortUpload}
      />
      <VideoUploadErrorBottomSheet
        modalState={errorModalState}
        errorText={videoErrorText}
        isLoading={isUploading}
        canRetryUpload={Boolean(videoBlob?.size)}
        onRetry={retryUpload}
        onClose={() => {
          resetUpload();
          errorModalState.closeModal();
          onCancel();
        }}
      />
    </>
  );
}
